<template>
  <div class="space-y-6">
    <div class="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h2 class="text-base font-semibold text-gray-900">Custom Fields</h2>
        <p class="text-sm text-gray-500 mt-0.5">Define the job fields collected for each service</p>
      </div>
      <div class="flex items-center gap-3">
        <span v-if="saved" class="text-sm text-green-600">
          <i class="fa-solid fa-check mr-1"></i>Saved
        </span>
        <span v-else-if="dirty" class="text-sm text-amber-600">Unsaved changes</span>
        <button
          @click="save()"
          :disabled="saving || !dirty"
          class="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-40 transition-colors"
        >
          <i v-if="saving" class="fa-solid fa-spinner fa-spin mr-1"></i>
          Save Changes
        </button>
      </div>
    </div>

    <div v-if="loading" class="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-400">
      <i class="fa-solid fa-spinner fa-spin mr-2"></i>Loading
    </div>

    <template v-else>
      <!-- Feature flag -->
      <div class="bg-white rounded-xl border border-gray-200 p-4 flex items-start justify-between gap-4">
        <div>
          <p class="text-sm font-medium text-gray-900">Enable per-service fields</p>
          <p class="text-xs text-gray-500 mt-0.5">
            While this is off, jobs, the schedule and the client portal behave exactly as they do
            today. Turn it on once the field sets below are correct.
          </p>
        </div>
        <button
          @click="defs.enabled = !defs.enabled"
          class="relative w-11 h-6 rounded-full transition-colors flex-shrink-0"
          :class="defs.enabled ? 'bg-primary' : 'bg-gray-200'"
        >
          <span
            class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
            :class="defs.enabled ? 'translate-x-5' : ''"
          ></span>
        </button>
      </div>

      <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div class="flex flex-col lg:flex-row">
          <!-- Service list -->
          <div class="lg:w-64 flex-shrink-0 border-b lg:border-b-0 lg:border-r border-gray-200">
            <div class="p-3 space-y-1">
              <button
                v-for="s in defs.services"
                :key="s.key"
                @click="selectedKey = s.key"
                class="w-full text-left px-3 py-2.5 rounded-lg transition-colors"
                :class="selectedKey === s.key ? 'bg-gray-100' : 'hover:bg-gray-50'"
              >
                <div class="flex items-center justify-between gap-2">
                  <span
                    class="text-sm font-medium truncate"
                    :class="s.active === false ? 'text-gray-400' : 'text-gray-900'"
                  >{{ s.label }}</span>
                  <span
                    v-if="s.active === false"
                    class="text-[10px] uppercase tracking-wide text-gray-400 flex-shrink-0"
                  >Off</span>
                </div>
                <p class="text-xs text-gray-400 mt-0.5">{{ (s.fields || []).length }} fields</p>
              </button>
            </div>
          </div>

          <!-- Field editor -->
          <div v-if="selected" class="flex-1 min-w-0 p-4 lg:p-5 space-y-4">
            <div class="flex items-end gap-3 flex-wrap">
              <div class="flex-1 min-w-[180px]">
                <label class="text-xs font-medium text-gray-500 block mb-1.5">Service name</label>
                <input
                  v-model="selected.label"
                  type="text"
                  class="w-full text-sm border border-gray-200 rounded-lg px-3 h-9 outline-none focus:border-indigo-400"
                />
              </div>
              <label class="flex items-center gap-2 h-9 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" :checked="selected.active !== false" @change="selected.active = $event.target.checked" />
                Available for new jobs
              </label>
            </div>
            <p class="text-xs text-gray-400 font-mono">{{ selected.key }}</p>

            <!-- Fields table -->
            <div class="border border-gray-200 rounded-lg overflow-x-auto">
              <table class="w-full text-sm">
                <thead class="bg-gray-50 text-xs text-gray-500">
                  <tr>
                    <th class="text-left font-medium px-3 py-2 w-8"></th>
                    <th class="text-left font-medium px-3 py-2 min-w-[200px]">Label</th>
                    <th class="text-left font-medium px-3 py-2 w-32">Type</th>
                    <th
                      v-for="surface in surfaces"
                      :key="surface.key"
                      class="font-medium px-2 py-2 w-20 text-center"
                    >{{ surface.label }}</th>
                    <th class="px-3 py-2 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!selected.fields.length">
                    <td :colspan="4 + surfaces.length" class="px-3 py-6 text-center text-sm text-gray-400">
                      No fields yet
                    </td>
                  </tr>
                  <template v-for="(f, idx) in selected.fields">
                    <tr :key="f.key" class="border-t border-gray-100">
                      <td class="px-2 py-2 align-top">
                        <div class="flex flex-col">
                          <button
                            @click="moveField(idx, -1)"
                            :disabled="idx === 0"
                            class="text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-300 leading-none"
                          >
                            <i class="fa-solid fa-caret-up"></i>
                          </button>
                          <button
                            @click="moveField(idx, 1)"
                            :disabled="idx === selected.fields.length - 1"
                            class="text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-300 leading-none"
                          >
                            <i class="fa-solid fa-caret-down"></i>
                          </button>
                        </div>
                      </td>
                      <td class="px-3 py-2 align-top">
                        <input
                          v-model="f.label"
                          type="text"
                          class="w-full text-sm border border-gray-200 rounded px-2 h-8 outline-none focus:border-indigo-400"
                        />
                        <p class="text-[11px] text-gray-400 font-mono mt-1">
                          {{ f.key }}
                          <span v-if="f.locked" class="text-gray-400 font-sans ml-1">
                            <i class="fa-solid fa-lock text-[9px]"></i> in use
                          </span>
                        </p>
                      </td>
                      <td class="px-3 py-2 align-top">
                        <select
                          v-model="f.type"
                          :disabled="f.locked"
                          class="w-full text-sm border border-gray-200 rounded px-2 h-8 outline-none focus:border-indigo-400 disabled:bg-gray-50 disabled:text-gray-400"
                        >
                          <option v-for="t in fieldTypes" :key="t.value" :value="t.value">{{ t.label }}</option>
                        </select>
                      </td>
                      <td
                        v-for="surface in surfaces"
                        :key="surface.key"
                        class="px-2 py-2 text-center align-top"
                      >
                        <input
                          type="checkbox"
                          class="mt-1.5"
                          :checked="!!(f.showOn && f.showOn[surface.key])"
                          @change="setShowOn(f, surface.key, $event.target.checked)"
                        />
                      </td>
                      <td class="px-3 py-2 text-right align-top">
                        <button
                          v-if="!f.locked"
                          @click="removeField(idx)"
                          class="text-gray-300 hover:text-red-500 transition-colors mt-1.5"
                          title="Delete field"
                        >
                          <i class="fa-solid fa-trash text-xs"></i>
                        </button>
                      </td>
                    </tr>
                    <tr v-if="f.type === 'select'" :key="f.key + '-options'" class="bg-gray-50">
                      <td></td>
                      <td :colspan="3 + surfaces.length" class="px-3 pb-3">
                        <label class="text-[11px] font-medium text-gray-500 block mb-1">Dropdown options (comma separated)</label>
                        <input
                          type="text"
                          :value="(f.options || []).join(', ')"
                          @change="setOptions(f, $event.target.value)"
                          placeholder="Option one, Option two"
                          class="w-full text-sm border border-gray-200 rounded px-2 h-8 outline-none focus:border-indigo-400"
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>

            <!-- Add field -->
            <div class="flex items-end gap-2 flex-wrap">
              <div class="flex-1 min-w-[180px]">
                <label class="text-xs font-medium text-gray-500 block mb-1.5">New field label</label>
                <input
                  v-model="newFieldLabel"
                  type="text"
                  placeholder="e.g. Core Diameter (mm)"
                  @keyup.enter="addField()"
                  class="w-full text-sm border border-gray-200 rounded-lg px-3 h-9 outline-none focus:border-indigo-400"
                />
              </div>
              <select
                v-model="newFieldType"
                class="text-sm border border-gray-200 rounded-lg px-3 h-9 outline-none focus:border-indigo-400"
              >
                <option v-for="t in fieldTypes" :key="t.value" :value="t.value">{{ t.label }}</option>
              </select>
              <button
                @click="addField()"
                :disabled="!newFieldLabel.trim()"
                class="px-4 h-9 bg-gray-900 text-white text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-40 transition-colors"
              >
                <i class="fa-solid fa-plus mr-1"></i>Add
              </button>
            </div>
            <p v-if="addError" class="text-xs text-red-500">{{ addError }}</p>
          </div>
        </div>
      </div>

      <p v-if="saveError" class="text-sm text-red-500">{{ saveError }}</p>
    </template>
  </div>
</template>

<script>
import {
  FIELD_TYPES,
  SURFACES,
  cloneDefaults,
  keyFromLabel,
  loadDefinitions,
  saveDefinitions,
} from "../../services/customFields";

export default {
  data() {
    return {
      loading: true,
      saving: false,
      saved: false,
      saveError: "",
      addError: "",
      defs: cloneDefaults(),
      snapshot: "",
      selectedKey: "",
      newFieldLabel: "",
      newFieldType: "text",
      fieldTypes: FIELD_TYPES,
      surfaces: SURFACES,
    };
  },
  computed: {
    selected() {
      return this.defs.services.find((s) => s.key === this.selectedKey) || null;
    },
    dirty() {
      return JSON.stringify(this.serialise()) !== this.snapshot;
    },
  },
  methods: {
    serialise() {
      return { enabled: !!this.defs.enabled, services: this.defs.services };
    },
    markClean() {
      this.snapshot = JSON.stringify(this.serialise());
    },
    setShowOn(field, surface, value) {
      if (!field.showOn) {
        this.$set(field, "showOn", { schedule: false, jobCard: false, clientPortal: false, tester: false });
      }
      this.$set(field.showOn, surface, value);
    },
    setOptions(field, raw) {
      const options = raw.split(",").map((o) => o.trim()).filter(Boolean);
      this.$set(field, "options", options);
    },
    moveField(idx, delta) {
      const target = idx + delta;
      if (target < 0 || target >= this.selected.fields.length) return;
      const fields = this.selected.fields;
      fields.splice(target, 0, fields.splice(idx, 1)[0]);
    },
    removeField(idx) {
      const field = this.selected.fields[idx];
      if (!confirm(`Delete "${field.label}"? Values already saved on jobs are kept but no longer shown.`)) return;
      this.selected.fields.splice(idx, 1);
    },
    addField() {
      this.addError = "";
      const label = this.newFieldLabel.trim();
      if (!label) return;
      const key = keyFromLabel(label);
      if (!key) {
        this.addError = "That label needs at least one letter or number.";
        return;
      }
      if (this.selected.fields.some((f) => f.key === key)) {
        this.addError = `"${label}" already exists on this service.`;
        return;
      }
      const field = {
        key,
        label,
        type: this.newFieldType,
        showOn: { schedule: false, jobCard: true, clientPortal: true, tester: true },
      };
      if (this.newFieldType === "select") field.options = [];
      this.selected.fields.push(field);
      this.newFieldLabel = "";
      this.newFieldType = "text";
    },
    async save() {
      this.saving = true;
      this.saved = false;
      this.saveError = "";
      // Keep `order` consistent with the displayed list.
      this.defs.services.forEach((s, i) => this.$set(s, "order", i));
      try {
        await saveDefinitions(this.defs);
        this.markClean();
        this.saved = true;
        setTimeout(() => { this.saved = false; }, 3000);
      } catch (e) {
        console.error("Failed to save custom fields:", e);
        this.saveError = "Could not save. Check your connection and try again.";
      }
      this.saving = false;
    },
  },
  async created() {
    const defs = await loadDefinitions();
    this.defs = { enabled: !!defs.enabled, services: defs.services };
    this.selectedKey = this.defs.services.length ? this.defs.services[0].key : "";
    this.markClean();
    this.loading = false;
  },
};
</script>
